import PageHeader from "@/components/site/PageHeader";
import PageTitle from "@/components/site/PageTitle";
import { motion } from "framer-motion";
import { Compass, Target, Zap, Users, ArrowUpRight, CheckCircle2 } from "lucide-react";
import KineticMarquee from "@/components/site/KineticMarquee";
import PartnersSection from "@/components/site/PartnersSection";
import IndustryExposure from "@/components/site/IndustryExposure";
import CTASection from "@/components/site/CTASection";
import { TEAM } from "@/lib/content";

const VALUES = [
  {
    icon: Compass,
    title: "Mission-led",
    body: "Every quarter we measure what we built and what it built into the student.",
  },
  {
    icon: Target,
    title: "Outcomes over hours",
    body: "We track capability — not seat-time. Rubrics, demos, projects, deployments.",
  },
  {
    icon: Zap,
    title: "Real hardware. Real code.",
    body: "Students wire real sensors, train real models, ship real apps. No simulators in disguise.",
  },
  {
    icon: Users,
    title: "Mentor-first",
    body: "Our edge is the mentor network — engineers, makers, ex-faculty. They show up.",
  },
];

function MentorCard({ m, index }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className="border border-[var(--ri-ink)] bg-white grid md:grid-cols-12"
      data-testid={`mentor-${index}`}
    >
      <div className="md:col-span-4 border-r-0 md:border-r border-b md:border-b-0 border-[var(--ri-ink)] p-7 md:p-8 flex flex-col justify-between bg-[var(--ri-bg)]">
        <div>
          <div className="aspect-square max-w-[220px] border border-[var(--ri-ink)] grid place-items-center bg-white relative overflow-hidden group">
            <div className="absolute inset-0 diagonal-stripes opacity-[0.06]" aria-hidden />
            {m.image ? (
              <img
                src={m.image}
                alt={m.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 z-10"
              />
            ) : (
              <span className="font-display text-7xl md:text-8xl tracking-tighter leading-none relative z-10">
                {m.initials}
              </span>
            )}
            <span className="absolute top-2 right-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--ri-muted)] z-20">
              ID · 0{index + 1}
            </span>
          </div>
          <div className="mt-6">
            <span className="ri-tag ri-tag--orange">{m.period}</span>
          </div>
        </div>
        <div className="mt-6">
          <div className="overline mb-2">Core skills</div>
          <div className="flex flex-wrap gap-2">
            {m.skills.map((s) => (
              <span key={s} className="ri-tag text-[10px]">
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="md:col-span-8 p-7 md:p-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ri-muted)]">
          {index === 0 ? "Mentor / Founder" : "Co-Mentor"}
        </div>
        <h3 className="mt-2 font-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
          {m.name}
        </h3>
        <div className="mt-2 font-display-soft text-lg md:text-xl text-[var(--ri-muted)]">
          {m.title}
        </div>

        <p className="mt-5 text-[15px] leading-relaxed">{m.summary}</p>

        <div className="mt-6">
          <div className="overline mb-3">Highlights</div>
          <ul className="space-y-2.5">
            {m.highlights.map((h) => (
              <li key={h} className="flex items-start gap-3 text-sm">
                <CheckCircle2
                  size={16}
                  className="text-[var(--ri-orange)] mt-0.5 flex-shrink-0"
                />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.article>
  );
}

export default function AboutPage() {
  return (
    <main data-testid="about-page">
      <PageTitle title="About" />
      <PageHeader
        eyebrow="About"
        code="04"
        kicker="Who we are"
        title={
          <>
            Gakuen Technologies.<br />
            A{" "}
            <span className="bg-[var(--ri-orange)] text-white px-3">
              future-tech
            </span>{" "}
            company.
          </>
        }
        lead="Gakuen Technologies Pvt Ltd builds technology for institutions. RoboInsci, our flagship innovation & training brand, runs the workshops, mentorship and lab programs that bring it to life."
      />

      {/* Brand architecture */}
      <section className="border-t border-[var(--ri-ink)] bg-white" data-testid="brand-architecture">
        <div className="ri-container py-20 md:py-28">
          <div className="overline mb-4"><span className="dot mr-2" /> 02 — Brand architecture</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] max-w-3xl mb-12">
            One company. Two clearly defined roles.
          </h2>
          <div className="grid md:grid-cols-2 gap-px bg-[var(--ri-ink)] border border-[var(--ri-ink)]">
            <div className="bg-white p-8 md:p-10" data-testid="brand-gakuen">
              <span className="ri-tag">Parent · Technology Provider</span>
              <h3 className="mt-5 font-display text-3xl md:text-4xl tracking-tight">
                Gakuen Technologies
              </h3>
              <p className="mt-4 text-[var(--ri-muted)] leading-relaxed">
                The website, the company identity and the technology platform — robotics kits, AI systems, IoT devices and lab infrastructure delivered to institutions across India and beyond.
              </p>
            </div>
            <div className="bg-[var(--ri-ink)] text-white p-8 md:p-10" data-testid="brand-roboinsci">
              <span className="ri-tag" style={{ background: "var(--ri-orange)", color: "#fff", borderColor: "var(--ri-orange)" }}>
                Brand · Innovation Ecosystem
              </span>
              <h3 className="mt-5 font-display text-3xl md:text-4xl tracking-tight">
                RoboInsci
              </h3>
              <p className="mt-4 text-white/70 leading-relaxed">
                The flagship initiative — workshops, training, mentorship and the innovation ecosystem that connects students to hackathons, incubation centers and the IIC pipeline.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision / Mission */}
      <section className="border-t border-[var(--ri-ink)] bg-[var(--ri-bg)]">
        <div className="ri-container py-20 md:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6 border border-[var(--ri-ink)] p-8 md:p-10 bg-white">
            <div className="overline mb-4"><span className="dot mr-2" /> Vision</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.02]">
              Every institution running a future-tech ecosystem — the same way every institution runs a library.
            </h2>
          </div>
          <div className="lg:col-span-6 border border-[var(--ri-ink)] p-8 md:p-10 bg-[var(--ri-ink)] text-white">
            <div className="overline text-white/50 mb-4"><span className="dot mr-2" /> Mission</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.02]">
              Robotics, AI, IoT and 3D-printing as <span className="text-[var(--ri-orange)]">programs</span> + <span className="text-[var(--ri-orange)]">products</span> — delivered, taught, supported.
            </h2>
          </div>
        </div>
      </section>

      {/* Values bento */}
      <section className="border-t border-[var(--ri-ink)] bg-[var(--ri-surface-alt)]">
        <div className="ri-container py-20 md:py-28">
          <div className="overline mb-4"><span className="dot mr-2" /> 04 — How we work</div>
          <h2 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95] max-w-3xl mb-12">
            Four principles. <span className="italic font-display-soft">Held tight.</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--ri-ink)] border border-[var(--ri-ink)]">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="bg-white p-7 md:p-8"
                  data-testid={`about-value-${i}`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <Icon size={28} strokeWidth={1.4} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--ri-muted)]">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl tracking-tight">{v.title}</h3>
                  <p className="mt-3 text-[var(--ri-muted)] text-sm leading-relaxed">{v.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <KineticMarquee />

      {/* Team / Mentors */}
      <section className="border-t border-[var(--ri-ink)] bg-white" data-testid="mentors-section">
        <div className="ri-container py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
            <div className="lg:col-span-7">
              <div className="overline mb-4"><span className="dot mr-2" /> 05 — Team & Mentorship</div>
              <h2 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95]">
                The people who{" "}
                <span className="italic font-display-soft">build</span>{" "}
                with us.
              </h2>
            </div>
            <div className="lg:col-span-5 flex items-end">
              <p className="text-[var(--ri-muted)] leading-relaxed">
                Decades of robotics experience. Thousands of mentored students. DRDO-shipped applications, university incubation cells and live-fire hackathon teams — that's the bench.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {TEAM.map((m, i) => (
              <MentorCard key={m.name} m={m} index={i} />
            ))}
          </div>

          <div className="mt-10 border border-[var(--ri-ink)] bg-[var(--ri-bg)] p-7 md:p-9 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="mentor-network-card">
            <div>
              <div className="overline mb-2">Mentor network</div>
              <div className="font-display text-2xl md:text-3xl leading-tight">
                Plus a wider bench of engineers, makers & ex-faculty across robotics, AI and IoT.
              </div>
            </div>
            <a href="/contact" className="btn-primary self-start" data-testid="mentor-network-cta">
              Talk to a mentor <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      <IndustryExposure />
      <PartnersSection />
      <CTASection />
    </main>
  );
}
